"use client";

import { useEffect, useRef } from "react";
import { useInView } from "motion/react";
import { annotate } from "rough-notation";
import type React from "react";

type AnnotationAction =
  | "highlight"
  | "underline"
  | "box"
  | "circle"
  | "strike-through"
  | "crossed-off"
  | "bracket";

interface HighlighterProps {
  children: React.ReactNode;
  action?: AnnotationAction;
  color?: string;
  darkColor?: string;
  strokeWidth?: number;
  animationDuration?: number;
  iterations?: number;
  padding?: number;
  multiline?: boolean;
  isView?: boolean;
}

export function Highlighter({
  children,
  action = "highlight",
  color = "#ffd1dc",
  darkColor,
  strokeWidth = 1.5,
  animationDuration = 600,
  iterations = 2,
  padding = 2,
  multiline = true,
  isView = false,
}: HighlighterProps) {
  const lightElementRef = useRef<HTMLSpanElement>(null);
  const darkElementRef = useRef<HTMLSpanElement>(null);

  const isInView = useInView(lightElementRef, {
    once: true,
    margin: "-10%",
  });

  // If isView is false, always show. If isView is true, wait for inView
  const shouldShow = !isView || isInView;

  // Create annotation for light mode
  useEffect(() => {
    if (!shouldShow) return;

    const element = lightElementRef.current;
    if (!element) return;

    const annotationConfig = {
      type: action,
      color: color,
      strokeWidth,
      animationDuration,
      iterations,
      padding,
      multiline,
    };

    const annotation = annotate(element, annotationConfig);
    annotation.show();

    const resizeObserver = new ResizeObserver(() => {
      annotation.hide();
      annotation.show();
    });

    resizeObserver.observe(element);
    resizeObserver.observe(document.body);

    return () => {
      annotation.remove();
      resizeObserver.disconnect();
    };
  }, [
    shouldShow,
    action,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
  ]);

  // Create annotation for dark mode (only if darkColor is provided)
  useEffect(() => {
    if (!shouldShow || !darkColor) return;

    const element = darkElementRef.current;
    if (!element) return;

    const annotationConfig = {
      type: action,
      color: darkColor,
      strokeWidth,
      animationDuration,
      iterations,
      padding,
      multiline,
    };

    const annotation = annotate(element, annotationConfig);
    annotation.show();

    const resizeObserver = new ResizeObserver(() => {
      annotation.hide();
      annotation.show();
    });

    resizeObserver.observe(element);
    resizeObserver.observe(document.body);

    return () => {
      annotation.remove();
      resizeObserver.disconnect();
    };
  }, [
    shouldShow,
    action,
    darkColor,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
  ]);

  // If no darkColor is provided, render single element
  if (!darkColor) {
    return (
      <span ref={lightElementRef} className="relative inline-block bg-transparent">
        {children}
      </span>
    );
  }

  // If darkColor is provided, render both and use Tailwind classes to show/hide
  return (
    <>
      <span ref={lightElementRef} className="dark:hidden relative inline-block bg-transparent">
        {children}
      </span>
      <span ref={darkElementRef} className="hidden dark:inline-block relative bg-transparent">
        {children}
      </span>
    </>
  );
}
